import { useState } from "react";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { toast } from "sonner";
import { ROLES, type BoardCard } from "@salesboard/contracts";
import { useIsDisconnected } from "@/app/ConnectionState";
import { useOpenDetail } from "@/app/useOpenDetail";
import { usePrefetchDetail } from "@/app/usePrefetchDetail";
import { ActiveFilterChips } from "@/components/ui/data-table/ActiveFilterChips";
import { EmptyState, ErrorState, NoResultState } from "@/components/ui/states";
import { useT } from "@/i18n";
import { ApiClientError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { SummaryStrip } from "@/features/analytics/SummaryStrip";
import { useSession } from "@/features/auth/useSession";
import { useOpportunityFilterDefs } from "@/features/opportunities/filterDefs";
import { BoardGrid, BoardGridSkeleton } from "./BoardGrid";
import { OpportunityCard } from "./OpportunityCard";
import { boardCollisionDetection } from "./drag/collision";
import { useBoard } from "./hooks";
import { useBoardParams } from "./useBoardParams";
import { useBoardDensity } from "./useBoardDensity";
import { useTvMode } from "./useTvMode";
import { pendingPlacement, useMoveFlow } from "./useMoveFlow";

export function BoardPage({
  onCreate,
  /**
   * Whether the summary strip is on screen — the pipeline header's own toggle, a display
   * preference the shell owns. Defaulted so the board renders complete wherever it is mounted
   * without a pipeline shell above it (tests, and any future embedding).
   */
  stripVisible = true,
}: {
  onCreate: () => void;
  stripVisible?: boolean;
}): React.JSX.Element {
  const t = useT();
  const openDetail = useOpenDetail();
  const prefetchDetail = usePrefetchDetail();
  const session = useSession();
  const filterDefs = useOpportunityFilterDefs();
  const params = useBoardParams(filterDefs);
  const board = useBoard(params.query);
  // Moving a card while other people's moves are invisible would be acting on figures that
  // may already be wrong, so dragging pauses until the stream is live again.
  const disconnected = useIsDisconnected();
  // Presented, the toolbar goes and its two facts — the year and whether the board is
  // filtered — become a static caption. The board must never silently lie about either.
  const { tv } = useTvMode();
  const { density } = useBoardDensity();

  const [dragging, setDragging] = useState<BoardCard>();

  const data = board.data;
  const flow = useMoveFlow({ stages: data?.stages, boardYear: data?.year ?? new Date().getFullYear() });

  const sensors = useSensors(
    // 5px activation is what lets a bare click reach the card without starting a drag.
    //
    // Pointer only. A `KeyboardSensor` was registered here and did nothing, because the card
    // never attaches dnd-kit's keyboard activator — it advertised a keyboard drag that dead-
    // ended. Keyboard parity is opening the card — reachable by Tab and Enter — and editing
    // its stage or date inline in the detail, which reaches every move a drop can.
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const isHeadOfSales = session.data?.role === ROLES.headOfSales;

  const onDragStart = (event: DragStartEvent): void => {
    setDragging((event.active.data.current as { card?: BoardCard } | undefined)?.card);
  };

  const onDragEnd = (event: DragEndEvent): void => {
    setDragging(undefined);
    const card = (event.active.data.current as { card?: BoardCard } | undefined)?.card;
    const target = event.over?.data.current as { stageId: string; quarter: number } | undefined;
    if (card && target) flow.begin(card, target);
  };

  if (board.isPending) {
    return (
      <>
        <BoardHeader params={params} tv={tv} stripVisible={stripVisible} />
        <BoardGridSkeleton />
      </>
    );
  }

  if (board.error || !data) {
    return (
      <>
        {/* The header stays: a reader whose board failed to load still needs the year
            selector and the filters — undoing one of them may be the way out. */}
        <BoardHeader params={params} tv={tv} stripVisible={stripVisible} />
        <ErrorState
          message={board.error instanceof ApiClientError ? board.error.message : t("board.loadFailed")}
          requestId={board.error instanceof ApiClientError ? board.error.requestId : "unknown"}
          onRetry={() => void board.refetch()}
        />
      </>
    );
  }

  return (
    <>
      <BoardHeader
        params={params}
        tv={tv}
        year={data.year}
        editable={data.editable}
        stripVisible={stripVisible}
      />

      {/* The grid always renders — a filtered-empty board must still show its stage rows,
          so a reader can see that a stage is empty rather than wonder if it exists. */}
      <DndContext
        sensors={sensors}
        // Pointer first, then rect intersection, then closest centre — see collision.ts for
        // why one strategy alone drifted away from the cursor.
        collisionDetection={boardCollisionDetection}
        onDragStart={onDragStart}
        // Escape mid-drag must clear the lifted card, or the overlay's state outlives the
        // gesture. It is invisible today only because dnd-kit stops rendering the overlay.
        onDragCancel={() => setDragging(undefined)}
        onDragEnd={onDragEnd}
      >
        <BoardGrid
          density={density}
          stages={data.stages}
          cards={data.items}
          cells={data.cells}
          rowTotals={data.rows}
          year={data.year}
          editable={data.editable && !disconnected}
          currentUserId={session.data?.id}
          isHeadOfSales={isHeadOfSales}
          pending={pendingPlacement(flow.pending)}
          // Over the board, not instead of it: the arrangement the reader is mid-meeting
          // with survives, and closing returns to it with filters intact.
          onOpenCard={(card) => openDetail(`/opportunity/${card.slug}`)}
          onHoverCard={(card) => prefetchDetail(`/opportunity/${card.slug}`, card.id)}
          onBlockedDrag={(card) =>
            toast.info(t("board.blockedDrag", card.ownerName))
          }
        />

        {/*
          `dropAnimation={null}` stays: the card's rest position is the *server's* answer,
          and an animated glide to a guessed slot would contradict the committed-result rule.
          The shadow is what reads as "lifted" — rotation alone reads as a glitch.
        */}
        <DragOverlay dropAnimation={null}>
          {dragging && (
            <div className="w-56 rotate-3 drop-shadow-2xl">
              <OpportunityCard card={dragging} movable={false} onOpen={() => undefined} />
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {data.items.length === 0 && params.activeFilterCount === 0 && (
        <div className="mt-4">
          <EmptyState
            title={t("board.emptyTitle", data.year)}
            description={t("board.emptyDescription")}
            action={{ label: t("pipeline.newOpportunity"), onClick: onCreate }}
          />
        </div>
      )}

      {data.items.length === 0 && params.activeFilterCount > 0 && (
        <div className="mt-4">
          <NoResultState
            // Values, not parameter names: "Filtering by Petr Dvořák" tells the reader what
            // to undo; "Filtering by owner" tells them the shape of the URL. The labels are
            // the chips' own, so the two cannot describe the same filter differently.
            activeFilters={params.activeFilterLabels}
            onClear={params.clearFilters}
            // The year comes from the response, not the scope hook: it is the year the
            // server actually answered for, which is the one these results are empty on.
            scope={t("board.noResultScope", data.year)}
          />
        </div>
      )}

      {/* Every move's confirmation, whether it started as a drag or in the Move dialog. */}
      {flow.dialogs}
    </>
  );
}

function BoardHeader({
  params,
  tv,
  year,
  editable = true,
  stripVisible,
}: {
  params: ReturnType<typeof useBoardParams>;
  /** Presented: the toolbar is replaced by a caption saying the same two things. */
  tv: boolean;
  year?: number;
  editable?: boolean;
  /** The pipeline header's strip toggle. Ignored while presented — see below. */
  stripVisible: boolean;
}) {
  const t = useT();
  const scopeYear = year ?? params.scope.year;

  /*
   * Whether this header has anything to draw at all.
   *
   * Its `mb-4` used to be unconditional, so a board with the strip off, nothing filtering and a
   * current year still paid 16px for an empty block — and the gap between the search row and the
   * grid was 28px where every other screen's is 12px (2026-08-31). Spacing belongs to content
   * that exists.
   */
  const hasContent = tv || stripVisible || params.activeFilterCount > 0 || editable === false;

  return (
    <div className={cn("space-y-3", hasContent && "mb-4")}>
      {/* No toolbar here since 2026-08-25: the pipeline shell renders one control row for
          both views — search, filters, year, view and the actions — so this component only
          reads the filters it is given. See specs/pipeline-header.md. */}
      {/*
        Presented, the filters stay *applied* but stop being editable — so the caption has
        to say which year is on screen and that something is filtering it. A viewer can
        infer neither from the grid, and a board that hides an active filter is a board
        telling the room a smaller number than the truth.
      */}
      {tv && (
        <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {scopeYear === undefined ? t("pipeline.title") : t("pipeline.titleYear", scopeYear)}
          </h1>
          {params.activeFilterCount > 0 && (
            <span className="bg-primary/10 text-primary rounded-full px-2.5 py-0.5 text-sm font-medium">
              {t("board.filteredWith", params.activeFilterLabels.join(" · "))}
            </span>
          )}
        </div>
      )}

      {/*
        The summary strip. It fills the slot this header reserved for it and **replaces** the
        plain "N on this board · CZK X" line that stood in: the Open and Pipeline tiles are
        those two figures, filtered identically, and openable.

        Presented, it renders **whatever the toggle says** — the strip *is* the meeting's
        numbers, a wall display showing a board with no totals is the one place they matter
        most, and the button that could hide them is not on the wall to press.
      */}
      {(tv || stripVisible) && <SummaryStrip filters={params} year={scopeYear} tv={tv} />}

      {/* The chips, where the `Filtered` pill used to be (2026-08-26). The pill said only
          *that* the board was filtered; the chips say what by, in the same place, and each one
          can be undone without opening the panel. Presented, they go with the rest of the
          chrome — a wall display is not a surface anyone edits filters on. */}
      {!tv && <ActiveFilterChips filters={params} />}

      {/* Only when there is a badge to hold. An always-rendered flex box is invisible until you
          measure the page and wonder where the gap came from. */}
      {!editable && (
        <div className={`flex flex-wrap items-center gap-3 ${tv ? "text-sm" : "text-xs"}`}>
          <span className="border-warning/40 bg-warning/10 rounded-full border px-2 py-0.5">
            {t("board.archiveYear")}
          </span>
        </div>
      )}
    </div>
  );
}
